import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { ConfirmDialog, EmptyState, FormDialog, ResponsiveTable, StatusChip } from './ui';

function formatWhen(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('pt-BR');
}

export default function PendingApprovals({
  requests,
  organizations,
  isAdmin,
  onApprove,
  onReject,
}) {
  const [approving, setApproving] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [role, setRole] = useState('funcionario');
  const [organizationId, setOrganizationId] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  if (!isAdmin) return null;

  const needsOrganization = role === 'funcionario' || role === 'gerente';

  const openApprove = (request) => {
    setApproving(request);
    setRole('funcionario');
    setOrganizationId('');
    setFormError('');
  };

  const submitApprove = async () => {
    if (!approving || submitting) return;
    if (needsOrganization && !organizationId) {
      setFormError('Selecione a organização.');
      return;
    }
    setSubmitting(true);
    setFormError('');
    try {
      await onApprove(approving.id, {
        tipo: role,
        organizacao_id: needsOrganization ? Number(organizationId) : (organizationId ? Number(organizationId) : null),
      });
      setApproving(null);
    } catch (error) {
      setFormError(error?.response?.data?.message || 'Não foi possível aprovar o cadastro.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitReject = async () => {
    if (!rejecting || submitting) return;
    setSubmitting(true);
    try {
      await onReject(rejecting.id, { rejection_reason: reason.trim() });
      setRejecting(null);
      setReason('');
    } catch (error) {
      setFormError(error?.response?.data?.message || 'Não foi possível rejeitar o cadastro.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minWidth: 0 }}>
      {requests.length === 0 ? (
        <EmptyState title="Nenhum cadastro pendente." description="Novas contas Google aparecem aqui até um administrador decidir." />
      ) : (
        <ResponsiveTable minWidth={760} label="Cadastros pendentes">
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>E-mail</TableCell>
              <TableCell>Provedor</TableCell>
              <TableCell>Data da solicitação</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.map((request) => (
              <TableRow key={request.id} hover>
                <TableCell>{request.nome}</TableCell>
                <TableCell>{request.email}</TableCell>
                <TableCell>{request.provider === 'google' ? 'Google' : request.provider}</TableCell>
                <TableCell>{formatWhen(request.approval_requested_at)}</TableCell>
                <TableCell><StatusChip status="pendente" label="Aguardando aprovação" /></TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Button size="small" variant="contained" onClick={() => openApprove(request)} aria-label={`Aprovar ${request.nome}`}>
                      Aprovar
                    </Button>
                    <Button size="small" variant="outlined" color="error" onClick={() => { setRejecting(request); setReason(''); setFormError(''); }} aria-label={`Rejeitar ${request.nome}`}>
                      Rejeitar
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </ResponsiveTable>
      )}

      <FormDialog
        open={Boolean(approving)}
        title="Aprovar cadastro"
        onClose={() => { if (!submitting) setApproving(null); }}
        onSubmit={submitApprove}
        submitting={submitting}
        submitLabel={submitting ? 'Aprovando...' : 'Aprovar cadastro'}
      >
        <Typography variant="body1">{approving?.nome}</Typography>
        <Typography variant="body2" color="text.secondary">{approving?.email}</Typography>
        <FormControl fullWidth>
          <InputLabel id="approval-role">Papel</InputLabel>
          <Select labelId="approval-role" label="Papel" value={role} onChange={(event) => setRole(event.target.value)}>
            <MenuItem value="funcionario">Funcionário</MenuItem>
            <MenuItem value="gerente">Gerente</MenuItem>
            <MenuItem value="admin">Administrador</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel id="approval-org">Organização</InputLabel>
          <Select
            labelId="approval-org"
            label="Organização"
            value={organizationId}
            onChange={(event) => setOrganizationId(event.target.value)}
          >
            {!needsOrganization ? <MenuItem value="">Sem organização</MenuItem> : null}
            {organizations.map((org) => (
              <MenuItem key={org.id} value={String(org.id)}>{org.nome}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {formError ? <Typography color="error" variant="body2">{formError}</Typography> : null}
      </FormDialog>

      <ConfirmDialog
        open={Boolean(rejecting)}
        title="Rejeitar cadastro"
        description={rejecting ? `${rejecting.nome} não poderá entrar até uma nova decisão administrativa.` : ''}
        confirmLabel={submitting ? 'Rejeitando...' : 'Rejeitar cadastro'}
        destructive
        confirming={submitting}
        onConfirm={submitReject}
        onClose={() => { if (!submitting) setRejecting(null); }}
      >
        <TextField
          label="Motivo interno"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          fullWidth
          multiline
          minRows={2}
          sx={{ mt: 2 }}
        />
        {formError ? <Typography color="error" variant="body2" sx={{ mt: 1 }}>{formError}</Typography> : null}
      </ConfirmDialog>
    </Box>
  );
}
